import React, { useState, useEffect } from 'react';
import { getFoundingMemberCount } from '../services/api';

const FOUNDING_MAX_SLOTS = 24;

interface CounterState {
  claimed: number;
  spots_remaining: number | null;
  cap_reached: boolean;
}

interface FoundingMemberCounterProps {
  tone?: 'onDark' | 'onLight';
}

// Live count of claimed Founding Member spots, fed by the FOUNDING50 coupon
// redemption count at GET /subscription/founding-member-count (server-cached ~60s).
// Renders nothing while loading, on request failure, or on a malformed payload —
// silent degradation only, never a broken "undefined of 24" state.
const FoundingMemberCounter: React.FC<FoundingMemberCounterProps> = ({ tone = 'onDark' }) => {
  const [data, setData] = useState<CounterState | null>(null);

  useEffect(() => {
    let active = true;
    getFoundingMemberCount()
      .then((res) => {
        if (!active) return;
        const d = (res && res.data) || {};

        // BE-1 renames the claimed count to spots_claimed; fall back to the
        // legacy times_redeemed field until that deploys. Without a real
        // number there is nothing to show.
        const claimed = (typeof d.spots_claimed === 'number' && Number.isFinite(d.spots_claimed))
          ? d.spots_claimed
          : d.times_redeemed;
        if (typeof claimed !== 'number' || !Number.isFinite(claimed)) {
          setData(null);
          return;
        }

        // Urgency/cap state is driven by the API's spots_remaining only —
        // FOUNDING_MAX_SLOTS is copy, never math.
        const spots_remaining = (typeof d.spots_remaining === 'number' && Number.isFinite(d.spots_remaining))
          ? d.spots_remaining
          : null;

        const cap_reached = (typeof d.cap_reached === 'boolean')
          ? d.cap_reached
          : (spots_remaining !== null && spots_remaining <= 0);

        setData({ claimed, spots_remaining, cap_reached });
      })
      .catch(() => { if (active) setData(null); });
    return () => { active = false; };
  }, []);

  if (!data) return null;

  const isUrgent = !data.cap_reached && data.spots_remaining !== null
    && data.spots_remaining > 0 && data.spots_remaining <= 5;

  let label: string;
  if (data.cap_reached) {
    label = `All ${FOUNDING_MAX_SLOTS} paid Founding Member spots claimed`;
  } else if (isUrgent) {
    label = `Only ${data.spots_remaining} paid Founding Member spots left`;
  } else {
    label = `${data.claimed} of ${FOUNDING_MAX_SLOTS} paid spots claimed`;
  }

  const stateKey: 'capped' | 'urgent' | 'normal' =
    data.cap_reached ? 'capped' : isUrgent ? 'urgent' : 'normal';

  const c = palette[tone][stateKey];

  return (
    <div style={tone === 'onDark' ? styles.rootDark : styles.rootLight}>
      <span style={{ ...styles.pill, color: c.text }}>
        <span style={{ ...styles.dot, background: c.dot }} />
        {label}
      </span>
    </div>
  );
};

const palette: Record<'onDark' | 'onLight', Record<'capped' | 'urgent' | 'normal', { text: string; dot: string }>> = {
  onDark: {
    normal: { text: 'rgba(255,255,255,0.78)', dot: '#34d399' },
    urgent: { text: '#fbbf24', dot: '#fbbf24' },
    capped: { text: 'rgba(255,255,255,0.55)', dot: 'rgba(255,255,255,0.4)' },
  },
  onLight: {
    normal: { text: '#475569', dot: '#10b981' },
    urgent: { text: '#b45309', dot: '#f59e0b' },
    capped: { text: '#94a3b8', dot: '#cbd5e1' },
  },
};

const styles: { [key: string]: React.CSSProperties } = {
  rootDark: { marginTop: '-14px', marginBottom: '30px' },
  rootLight: { marginTop: '12px' },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
};

export default FoundingMemberCounter;
